import { MouseEvent } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import CustomHelmet from "../../components/CustomHelmet";
import logoImage from "../../../media/images/logos/KarenTarabarNet-App.png";
import BaleIcon from "../../../media/images/icons/bale.png";
import RubikaIcon from "../../../media/images/icons/rubika.png";

const BAZAAR_LINK =
  "http://cafebazaar.ir/app/?id=com.hvn7.karenbar&ref=share";

const CHANNELS = [
  {
    name: "تلگرام",
    description: "عضویت در کانال رسمی کارن ترابر نت",
    href: "https://t.me/karentarabarnet",
    backgroundColor: "#229ED9",
    textColor: "#FFFFFF",
    iconBackground: "rgba(255, 255, 255, 0.16)",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path
          d="M21.05 3.58 3.68 10.27c-1.18.47-1.17 1.12-.22 1.41l4.45 1.39 10.3-6.5c.49-.3.93-.13.57.19l-8.33 7.53-.31 4.64c.45 0 .65-.2.9-.44l2.16-2.1 4.49 3.31c.83.46 1.42.22 1.63-.77l2.95-13.9c.3-1.21-.46-1.76-1.22-1.45Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    name: "بله",
    description: "عضویت در کانال کارن ترابر نت در بله",
    href: "https://ble.ir/join/8ELJbyZqgv",
    backgroundColor: "#FFFFFF",
    textColor: "#087F68",
    iconBackground: "#E8F8F3",
    icon: (
      <Box
        component="img"
        src={BaleIcon}
        alt="لوگوی بله"
        sx={{ width: 30, height: 30, objectFit: "contain" }}
      />
    ),
  },
  {
    name: "روبیکا",
    description: "عضویت در کانال کارن ترابر نت در روبیکا",
    href: "https://rubika.ir/karen_tarabarnet",
    backgroundColor: "#FFFFFF",
    textColor: "#262626",
    iconBackground: "#F3F3F3",
    icon: (
      <Box
        component="img"
        src={RubikaIcon}
        alt="لوگوی روبیکا"
        sx={{ width: 30, height: 30, objectFit: "contain" }}
      />
    ),
  },
] as const;

export default function PublicWelcome() {
  const theme = useTheme();

  const handleExternalLink = (
    event: MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    if (window.Native && typeof window.Native.openUrl === "function") {
      event.preventDefault();
      window.Native.openUrl(href);
    }
  };

  return (
    <>
      <CustomHelmet title="کانال‌های رسمی و دانلود اپلیکیشن" />

      <Box
        dir="rtl"
        sx={{
          minHeight: "100vh",
          overflowY: "auto",
          background: `linear-gradient(180deg, ${theme.palette.primary[50]} 0%, ${theme.palette.bgColor.grey100} 46%, ${theme.palette.bgColor.grey100} 100%)`,
          px: "16px",
          py: "24px",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 410,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "18px",
          }}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: "24px",
              backgroundColor: "bgColor.white",
              border: "1px solid rgba(23, 131, 240, 0.10)",
              boxShadow: "0 14px 36px rgba(16, 93, 170, 0.10)",
              px: "18px",
              pt: "22px",
              pb: "20px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 150,
                height: 150,
                borderRadius: "50%",
                backgroundColor: "rgba(23, 131, 240, 0.07)",
                top: -92,
                right: -54,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                width: 100,
                height: 100,
                borderRadius: "50%",
                backgroundColor: "rgba(217, 144, 54, 0.08)",
                bottom: -62,
                left: -34,
              }}
            />

            <Box
              sx={{
                position: "relative",
                width: "fit-content",
                maxWidth: "100%",
                mx: "auto",
                p: "8px 14px",
                borderRadius: "18px",
                backgroundColor: "bgColor.grey100",
                border: "1px solid rgba(0, 0, 0, 0.04)",
              }}
            >
              <Box
                component="img"
                src={logoImage}
                alt="کارن ترابر نت"
                sx={{
                  display: "block",
                  width: "210px",
                  maxWidth: "100%",
                  height: "auto",
                }}
              />
            </Box>

            <Typography
              component="h1"
              sx={{
                position: "relative",
                mt: "18px",
                color: "textColor.dark",
                fontSize: "22px",
                fontWeight: 800,
                lineHeight: 1.7,
              }}
            >
              به کارن ترابر نت خوش آمدید
            </Typography>

            <Typography
              sx={{
                position: "relative",
                mt: "6px",
                color: "textColor.light",
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: 2,
              }}
            >
              برای دریافت سریع اطلاعیه‌ها، اخبار و به‌روزرسانی‌ها به کانال‌های
              رسمی ما بپیوندید.
            </Typography>
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                mb: "10px",
                px: "2px",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: "textColor.dark",
                    fontSize: "16px",
                    fontWeight: 800,
                  }}
                >
                  کانال‌های رسمی ما
                </Typography>
                <Typography
                  sx={{
                    mt: "2px",
                    color: "textColor.light",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  ابتدا یکی از کانال‌ها را انتخاب کنید
                </Typography>
              </Box>

              <Box
                sx={{
                  px: "10px",
                  py: "5px",
                  borderRadius: "20px",
                  backgroundColor: "primary.50",
                  color: "primary.main",
                  fontSize: "11px",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                }}
              >
                پیشنهاد ما
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {CHANNELS.map((channel) => (
                <Box
                  key={channel.name}
                  component="a"
                  href={channel.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event: MouseEvent<HTMLAnchorElement>) =>
                    handleExternalLink(event, channel.href)
                  }
                  sx={{
                    minHeight: 74,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    p: "12px",
                    borderRadius: "18px",
                    backgroundColor: channel.backgroundColor,
                    color: channel.textColor,
                    border:
                      channel.name === "تلگرام"
                        ? "1px solid transparent"
                        : "1px solid rgba(0, 0, 0, 0.07)",
                    boxShadow:
                      channel.name === "تلگرام"
                        ? "0 10px 24px rgba(34, 158, 217, 0.22)"
                        : "0 8px 22px rgba(0, 0, 0, 0.06)",
                    transition:
                      "transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow:
                        channel.name === "تلگرام"
                          ? "0 13px 30px rgba(34, 158, 217, 0.28)"
                          : "0 12px 28px rgba(0, 0, 0, 0.09)",
                    },
                    "&:active": {
                      transform: "scale(0.99)",
                      opacity: 0.94,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "14px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: channel.iconBackground,
                      color: channel.textColor,
                    }}
                  >
                    {channel.icon}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: channel.textColor,
                        fontSize: "15px",
                        fontWeight: 800,
                      }}
                    >
                      {channel.name}
                    </Typography>
                    <Typography
                      sx={{
                        mt: "2px",
                        color: channel.textColor,
                        opacity: channel.name === "تلگرام" ? 0.88 : 0.62,
                        fontSize: "11px",
                        fontWeight: 600,
                        lineHeight: 1.8,
                      }}
                    >
                      {channel.description}
                    </Typography>
                  </Box>

                  <OpenInNewRoundedIcon
                    sx={{ fontSize: 20, opacity: channel.name === "تلگرام" ? 1 : 0.55 }}
                  />
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              mt: "2px",
              p: "14px",
              borderRadius: "20px",
              backgroundColor: "bgColor.white",
              border: "1px solid rgba(217, 144, 54, 0.18)",
              boxShadow: "0 8px 22px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Typography
              sx={{
                color: "textColor.dark",
                fontSize: "15px",
                fontWeight: 800,
              }}
            >
              اپلیکیشن کارن ترابر نت
            </Typography>
            <Typography
              sx={{
                mt: "4px",
                mb: "12px",
                color: "textColor.light",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: 1.9,
              }}
            >
              نسخه اندروید را می‌توانید از کافه‌بازار نصب یا به‌روزرسانی کنید.
            </Typography>

            <Box
              component="a"
              href={BAZAAR_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event: MouseEvent<HTMLAnchorElement>) =>
                handleExternalLink(event, BAZAAR_LINK)
              }
              sx={{
                width: "100%",
                minHeight: 58,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                px: "14px",
                py: "10px",
                borderRadius: "15px",
                color: "#FFFFFF",
                background:
                  "linear-gradient(135deg, #1F9D70 0%, #16845E 100%)",
                boxShadow: "0 9px 22px rgba(31, 157, 112, 0.20)",
                transition: "transform 0.18s ease, opacity 0.18s ease",
                "&:hover": { transform: "translateY(-1px)" },
                "&:active": { transform: "scale(0.99)", opacity: 0.94 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                  }}
                >
                  <StorefrontRoundedIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "14px", fontWeight: 800 }}>
                    نصب از کافه‌بازار
                  </Typography>
                  <Typography
                    sx={{ mt: "1px", fontSize: "10px", fontWeight: 600, opacity: 0.86 }}
                  >
                    مشاهده صفحه اپلیکیشن
                  </Typography>
                </Box>
              </Box>

              <OpenInNewRoundedIcon sx={{ fontSize: 20, opacity: 0.9 }} />
            </Box>
          </Box>

          <Typography
            sx={{
              pb: "8px",
              color: "textColor.light",
              textAlign: "center",
              fontSize: "10px",
              fontWeight: 600,
              lineHeight: 1.8,
            }}
          >
            کارن ترابر نت؛ بازارگاه حمل‌ونقل هوشمند ایران
          </Typography>
        </Box>
      </Box>
    </>
  );
}
